import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { ApiError } from "../../api/client";
import { RbacApi, type Permission } from "../rbac/api";
import { ScreenApi, type Screen } from "./api";

export function ScreensMatrix() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const screens = useQuery({ queryKey: ["screens"], queryFn: ScreenApi.list });
  const perms = useQuery({ queryKey: ["permissions"], queryFn: RbacApi.listPermissions });

  const remove = useMutation({
    mutationFn: ScreenApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["screens"] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });

  return (
    <div className="hierarchy">
      <header>
        <h1>Screens</h1>
        <p className="muted">
          Map each screen to the permissions it requires. The menu/UI hides anything the user can't access.
        </p>
      </header>
      {error && <div role="alert" className="error">{error}</div>}

      <NewScreenForm
        permissions={perms.data ?? []}
        onCreated={() => qc.invalidateQueries({ queryKey: ["screens"] })}
      />

      <ul className="tree">
        {screens.data?.items.map((s) => (
          <ScreenRow
            key={s.id}
            screen={s}
            permissions={perms.data ?? []}
            onSaved={() => qc.invalidateQueries({ queryKey: ["screens"] })}
            onDelete={() => remove.mutate(s.id)}
          />
        ))}
      </ul>
    </div>
  );
}

function ScreenRow({
  screen,
  permissions,
  onSaved,
  onDelete,
}: {
  screen: Screen;
  permissions: Permission[];
  onSaved: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [policy, setPolicy] = useState(screen.policy);
  const [selected, setSelected] = useState<Set<string>>(new Set(screen.requiredPermissions));

  const save = useMutation({
    mutationFn: () =>
      ScreenApi.update(screen.id, { policy, requiredPermissions: [...selected] }),
    onSuccess: () => { setOpen(false); onSaved(); },
  });

  return (
    <li>
      <div className="row">
        <span>
          <strong>{screen.title}</strong>{" "}
          <span className="muted">({screen.route})</span>
          <div className="muted" style={{ fontSize: 12 }}>
            policy={screen.policy} · {screen.requiredPermissions.length} permission(s)
          </div>
        </span>
        <span className="row-actions">
          <button onClick={() => setOpen((v) => !v)}>{open ? "Close" : "Edit"}</button>
          <button
            className="danger"
            onClick={() => {
              if (window.confirm("Remove this screen from the registry? Any menu items pointing at it will become invisible. This is a config-level change, not a data delete."))
                onDelete();
            }}
          >Remove</button>
        </span>
      </div>
      {open && (
        <div className="children">
          <div className="row-3">
            <label>
              Policy:
              <select value={policy} onChange={(e) => setPolicy(e.target.value as Screen["policy"])}>
                <option value="ALL">ALL — user must hold every listed permission</option>
                <option value="ANY">ANY — one is enough</option>
              </select>
            </label>
            <button
              onClick={() => {
                if (window.confirm(`Save changes to "${screen.title}"? Anyone whose roles no longer satisfy the new policy will lose access on their next refresh.`))
                  save.mutate();
              }}
              disabled={save.isPending}
            >
              {save.isPending ? "Saving…" : "Save"}
            </button>
          </div>
          <PermissionMatrix
            all={permissions}
            selected={selected}
            onToggle={(k) => {
              const next = new Set(selected);
              next.has(k) ? next.delete(k) : next.add(k);
              setSelected(next);
            }}
          />
        </div>
      )}
    </li>
  );
}

function NewScreenForm({
  permissions,
  onCreated,
}: {
  permissions: Permission[];
  onCreated: () => void;
}) {
  const [id, setId] = useState("");
  const [key, setKey] = useState("");
  const [route, setRoute] = useState("");
  const [title, setTitle] = useState("");
  const [policy, setPolicy] = useState<Screen["policy"]>("ALL");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: ScreenApi.create,
    onSuccess: () => {
      setId(""); setKey(""); setRoute(""); setTitle(""); setSelected(new Set());
      onCreated();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : "Failed"),
  });

  return (
    <form
      className="create-role"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate({
          id, key, route, title, policy,
          requiredPermissions: [...selected],
        });
      }}
    >
      <div className="row-3">
        <input value={id} onChange={(e) => setId(e.target.value)} placeholder="screen.id (dot.case)" required />
        <input value={key} onChange={(e) => setKey(e.target.value)} placeholder="route key (e.g. admin/cases)" required />
        <input value={route} onChange={(e) => setRoute(e.target.value)} placeholder="/admin/cases" required />
      </div>
      <div className="row-3" style={{ marginTop: 8 }}>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Display title" required />
        <select value={policy} onChange={(e) => setPolicy(e.target.value as Screen["policy"])}>
          <option value="ALL">Policy: ALL</option>
          <option value="ANY">Policy: ANY</option>
        </select>
        <button type="submit" disabled={create.isPending}>
          {create.isPending ? "Creating…" : "Create screen"}
        </button>
      </div>
      <PermissionMatrix
        all={permissions}
        selected={selected}
        onToggle={(k) => {
          const next = new Set(selected);
          next.has(k) ? next.delete(k) : next.add(k);
          setSelected(next);
        }}
      />
      {err && <div className="error" style={{ marginTop: 8 }}>{err}</div>}
    </form>
  );
}

function PermissionMatrix({
  all,
  selected,
  onToggle,
}: {
  all: Permission[];
  selected: Set<string>;
  onToggle: (k: string) => void;
}) {
  const grouped = useMemo(() => {
    const m: Record<string, Permission[]> = {};
    for (const p of all) (m[p.resource] ??= []).push(p);
    for (const list of Object.values(m)) list.sort((a, b) => a.action.localeCompare(b.action));
    return m;
  }, [all]);

  return (
    <div className="perm-matrix">
      {Object.entries(grouped)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([resource, list]) => (
          <fieldset key={resource}>
            <legend>{resource}</legend>
            {list.map((p) => (
              <label key={p.id} title={p.description}>
                <input
                  type="checkbox"
                  checked={selected.has(p.id)}
                  onChange={() => onToggle(p.id)}
                />
                {p.action}
              </label>
            ))}
          </fieldset>
        ))}
    </div>
  );
}
